import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import styles from './Events.module.scss'
import { BOLT_BACKEND_URL, NODE_ENV, WEBSITE_URL } from 'constants/envs'
import { SalesEvent } from 'types/sales'
import { useGetSalesEventsQuery } from 'store/queries/bolt-api/sales'
import LoadingDots from 'components/loading-dots/LoadingDots'
import { stringify } from 'qs'
import { useStoreSelector } from 'hooks/store.ts'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import { Loader, Tag } from '@boltenergy-be/design-system'
import Cookies from 'js-cookie'
import { ACCESS_COOKIE_NAME } from 'pages/sales/events/events.constants.ts'
import { useState } from 'react'
import classNames from 'classnames'
import { getAccessToken } from 'utils/localStorageService.ts'
import { UserTypes } from 'store/auth/types.ts'
import { redirect } from 'react-router'
import { routes } from 'types/routes.ts'
import { SalesGetWebsiteAuthTokenResponseBody } from '@boltenergy-be/shared'

const Events = () => {
  const { partner, ...salesUser } = useStoreSelector((store) => store.sales)
  const { data, isLoading, isError } = useGetSalesEventsQuery()

  // i18n
  const { t } = useTranslation(['home', 'common', 'errors'])

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  /**
   * Opens the event link of the given event
   *
   * @param {SalesEvent} event
   */
  const openEventLink = async (event: SalesEvent) => {
    setLoading(true)

    const accessToken = Cookies.get(ACCESS_COOKIE_NAME)

    try {
      if (!accessToken) {
        const currentPortalSessionToken = getAccessToken(UserTypes.SALES)

        if (!currentPortalSessionToken?.id) {
          redirect(routes.LOGIN_SALES)
          throw new Error('Access token missing')
        }

        // Use fetch to omit axios interceptors
        const response = await fetch(`${BOLT_BACKEND_URL}/auth/sales/website-token`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${currentPortalSessionToken?.id}`
          }
        })
        const { data }: { success: boolean; data: SalesGetWebsiteAuthTokenResponseBody } = await response.json()

        if (!data?.token?.id) {
          throw new Error('Could not generate salesWebsite token')
        }

        Cookies.set(ACCESS_COOKIE_NAME, data.token.id, {
          secure: true,
          sameSite: 'Strict',
          domain: NODE_ENV === 'local' ? 'localhost' : '.boltenergie.be',
          expires: new Date(data.token.expiresAt * 1000)
        })
      }

      const query = stringify(
        {
          ...event.parameters,
          agent_id: salesUser?.agentId || '',
          event_id: event.eventId,
          event_name: event.eventName,
          partner_id: partner?.id || ''
        },
        { skipNulls: true, addQueryPrefix: true }
      )

      const url = `${WEBSITE_URL}/${salesUser?.language}${query}`
      window.open(url, '_blank')?.focus()
    } catch (err) {
      setError(true)
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Formats the arrays into a string if applicable
   *
   * @param {string[] | string} array
   */
  const formatArrays = (array: string[] | string) => {
    if (Array.isArray(array) && array.every((item) => typeof item === 'string')) {
      return array.join(' ')
    }
    return array
  }

  return (
    <PageLayout
      title={t(($) => $.nav.events, {
        ns: 'common'
      })}
    >
      <section className={styles.events}>
        {isError && (
          <p>
            {t(($) => $.default, {
              ns: 'errors'
            })}
          </p>
        )}

        {!!data && (
          <>
            {data?.events && data.events?.length > 0 ? (
              data.events
                .filter((event: SalesEvent) => !event.hidden)
                .map((event: SalesEvent) => (
                  <button
                    key={event.eventId}
                    className={classNames(styles['event-card'], { [styles.loading]: loading })}
                    onClick={() => openEventLink(event)}
                    disabled={loading || error}
                  >
                    {loading && (
                      <div className={styles['event-loading-overlay']}>
                        <Loader />
                      </div>
                    )}
                    <strong className={styles['event-title']}>
                      {event.eventId} | {event.eventName}
                    </strong>

                    <div className={styles.parameters}>
                      <Tag type="neutral" size="small">
                        {formatArrays(event.parameters.simulation_so)}
                      </Tag>
                      <Tag type="neutral" size="small">
                        {formatArrays(event.parameters.sign_channel)}
                      </Tag>
                      <Tag type="neutral" size="small">
                        {formatArrays(event.parameters.simulation_type)}
                      </Tag>
                    </div>

                    {(event.eventStartDate || event.eventEndDate) && (
                      <small>
                        {event.eventStartDate && dayjs(event.eventStartDate).format('DD/MM/YYYY')}
                        {event.eventStartDate && event.eventEndDate && ' - '}
                        {event.eventEndDate && dayjs(event.eventEndDate).format('DD/MM/YYYY')}
                      </small>
                    )}
                  </button>
                ))
            ) : (
              <p>{t(($) => $.sales.home.noEvents)}</p>
            )}
          </>
        )}

        {!data && isLoading && <LoadingDots />}
      </section>
    </PageLayout>
  )
}

export default Events
